import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone || !form.message) {
      toast.error("Bitte alle Felder ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Anfrage übermittelt.");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Übermittlung fehlgeschlagen. Bitte erneut versuchen.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#F9FAFB]"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Left column */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A3B2C] mb-4">
              Kontakt
            </div>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] text-[#0A3B2C] mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="contact-title"
            >
              Potenzialgespräch anfragen.
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-10">
              Sie sind Stadtwerk, Gemeindewerk oder regionaler Versorger?
              Sprechen Sie mit uns – unverbindlich und vertraulich.
            </p>

            <div className="space-y-5">
              <ContactInfo icon={MapPin} title="Adresse" lines={[COMPANY.street, COMPANY.city]} />
              <ContactInfo
                icon={Phone}
                title="Telefon"
                lines={[COMPANY.phone]}
                href={`tel:${COMPANY.phoneRaw}`}
                testId="contact-phone-link"
              />
              <ContactInfo
                icon={Mail}
                title="E-Mail"
                lines={[COMPANY.email]}
                href={`mailto:${COMPANY.email}`}
                testId="contact-email-link"
              />
            </div>
          </div>

          {/* Right - form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field
                  label="Name"
                  value={form.name}
                  onChange={(v) => set("name", v)}
                  testId="contact-input-name"
                  placeholder="Max Mustermann"
                />
                <Field
                  label="Unternehmen"
                  value={form.company}
                  onChange={(v) => set("company", v)}
                  testId="contact-input-company"
                  placeholder="Stadtwerke Musterstadt"
                />
                <Field
                  type="email"
                  label="E-Mail"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                  testId="contact-input-email"
                  placeholder="name@versorger.de"
                />
                <Field
                  type="tel"
                  label="Telefon"
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  testId="contact-input-phone"
                  placeholder="+49 ..."
                />
              </div>

              <div className="mt-5">
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
                  Nachricht
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={5}
                  data-testid="contact-input-message"
                  placeholder="Was können wir für Sie tun?"
                  className="w-full px-4 py-3 rounded-md border border-gray-200 focus:border-[#0A3B2C] focus:ring-2 focus:ring-[#0A3B2C]/10 outline-none transition text-sm resize-none"
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-gray-500 max-w-md">
                  Mit dem Absenden akzeptieren Sie unsere{" "}
                  <a href="/datenschutz" className="text-[#0A3B2C] underline hover:no-underline">
                    Datenschutzerklärung
                  </a>
                  .
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-[#0A3B2C] text-white hover:bg-[#072A1F] disabled:opacity-60 disabled:cursor-not-allowed px-6 py-3 font-semibold rounded-md text-sm shadow-sm hover:shadow-md transition-all"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Wird gesendet…
                    </>
                  ) : (
                    <>
                      Anfrage senden
                      <Send className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testId, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-widest text-gray-500 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-md border border-gray-200 focus:border-[#0A3B2C] focus:ring-2 focus:ring-[#0A3B2C]/10 outline-none transition text-sm"
      />
    </div>
  );
}

function ContactInfo({ icon: Icon, title, lines, href, testId }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-[#0A3B2C]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-1">
          {title}
        </div>
        {lines.map((l, i) => (
          <div key={i} className="text-sm md:text-base text-gray-800">
            {l}
          </div>
        ))}
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} className="block hover:opacity-80 transition-opacity">
        {content}
      </a>
    );
  }
  return content;
}
